import React, { ReactElement, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { isSystemDowngraded } from 'utils/helpers/helpers';
import styles from './ProfileImg.module.scss';

export interface Props {
    imageUrl?: string;
    imageReplacementText?: string[];
}

// Maximální počet pokusů o načtení obrázku
const MAX_RETRIES = 2;
// Základní doba čekání mezi pokusy (ms)
const BASE_RETRY_DELAY = 500;

const ProfileImg = ({
    imageUrl,
    imageReplacementText,
}: Props): ReactElement => {
    // Stav pro sledování, zda byl obrázek úspěšně načten
    const [imageLoaded, setImageLoaded] = useState(false);

    // Použití useRef pro uchování počtu pokusů mezi rendery
    const retryCount = useRef(0);
    const retryTimerRef = useRef<NodeJS.Timeout | null>(null);

    const replaceImageWithText =
        imageReplacementText && imageReplacementText.length === 2;

    // Funkce pro načtení obrázku s retry logikou
    const loadImageWithRetry = (url: string, attempt = 0) => {
        // Zrušíme předchozí timer, pokud existuje
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }

        const img = new Image();

        img.onload = () => {
            // Obrázek byl úspěšně načten
            console.log(`Obrázek úspěšně načten (pokus: ${attempt + 1}):`, url);
            setImageLoaded(true);
            retryCount.current = 0; // Reset počítadla pokusů
        };

        img.onerror = () => {
            console.log(
                `Nepodařilo se načíst obrázek (pokus: ${attempt + 1}):`,
                url
            );

            // Pokud jsme ještě nevyčerpali všechny pokusy, zkusíme to znovu s větším odstupem
            if (attempt < MAX_RETRIES - 1) {
                const nextAttempt = attempt + 1;

                // Exponenciální odstup mezi pokusy (1s, 2s, 4s, ...)
                const delay = BASE_RETRY_DELAY * Math.pow(2, attempt);

                console.log(
                    `Zkusím znovu načíst obrázek za ${delay}ms (pokus: ${
                        nextAttempt + 1
                    })`
                );

                // Nastavíme timer pro další pokus
                retryTimerRef.current = setTimeout(() => {
                    loadImageWithRetry(url, nextAttempt);
                }, delay);
            } else {
                // Vyčerpali jsme všechny pokusy
                console.log(
                    'Vyčerpány všechny pokusy o načtení obrázku, zobrazuji defaultní obrázek'
                );
                setImageLoaded(false);
                retryCount.current = 0;
            }
        };

        // Přidání parametru s časovým razítkem pro obejití cache
        const cacheBuster = `?t=${new Date().getTime()}`;
        img.src = url + cacheBuster;
    };

    // Kdykoliv se změní imageUrl, resetujeme stav načtení a pokusíme se načíst nový obrázek
    useEffect(() => {
        // Pokud nemáme URL, nemáme co načítat
        if (!imageUrl) {
            setImageLoaded(false);
            return;
        }

        // Reset stavu při změně URL
        setImageLoaded(false);
        retryCount.current = 0;

        // Spustíme načítání obrázku s retry logikou
        loadImageWithRetry(imageUrl);

        // Cleanup
        return () => {
            // Zrušíme timer při unmount
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    }, [imageUrl]);

    // Zobrazíme defaultní obsah, pokud nemáme URL nebo obrázek ještě nebyl úspěšně načten
    const shouldShowDefault = !imageUrl || !imageLoaded;

    // Určení backgroundImage stylu
    const backgroundImageStyle = {
        backgroundImage: shouldShowDefault
            ? replaceImageWithText
                ? undefined
                : `url('/images/logo_blue.svg')`
            : `url('${imageUrl}')`,
    };

    return (
        <div
            style={backgroundImageStyle}
            className={classNames(styles.profileImage, {
                [styles.logo]: shouldShowDefault && !replaceImageWithText,
                [styles.downgraded]: isSystemDowngraded(),
            })}
        >
            {replaceImageWithText && shouldShowDefault && (
                <div className={styles.text}>
                    {imageReplacementText.map((text) => (
                        <div key={text}>{text}</div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProfileImg;
